import * as firebase from "firebase/app";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Highlighter from "react-highlight-words";
import { TiArrowSortedDown } from "react-icons/ti";
import Moment from "react-moment";
import { BarLoader } from "react-spinners";
import { useFirestore } from "reactfire";
import { useAppUser } from "../../../../../contexts/user-context/UserContext";
import Pagination from "../../../../../shared/components/ui/pagination/Pagination";
import {
  Country,
  PublicTeam,
  RequestStatus,
} from "../../../../../shared/constants/Constant";
import PageHeader from "../../../../dashboard/components/page-header/PageHeader";
import SessionPreview from "../../components/session-preview/SessionPreview";
import StatusLabelRequest from "../../components/status-label/StatusLabelRequest";
import "./styles.scss";
import { useWorkshops } from "../../../../../contexts/workshops-context/WorkshopsContext";

enum MRequestStatus {
  STATUS = "",
  ALL = "ALL",
  PENDING = "PENDING",
  SCHEDULED = "SCHEDULED",
  REJECTED = "REJECTED",
  DELETED = "DELETED",
  COMPLETED = "COMPLETED",
}

export enum MCountry {
  COUNTRY = "",
  ALL = "ALL",
  SRI_LANKA = "SRI_LANKA",
  MALAYSIA = "MALAYSIA",
  BOSNIA = "BOSNIA",
  JAMAICA = "JAMAICA",
  INDIA = "INDIA",
  INDONESIA = "INDONESIA",
}

export interface SessionRequest {
  id: string;
  mentorId: string;
  teamId: string;
  teamName: string;
  memberId: string;
  mentorName: string;
  status: RequestStatus;
  country: Country;
  createdAt: firebase.firestore.Timestamp;
  from?: "REQUEST" | "SESSION";
  endDate?: firebase.firestore.Timestamp;
}

interface Filter {
  mentorName: string;
  status: MRequestStatus;
  country: MCountry;
}

const initialFilters: Filter = {
  status: MRequestStatus.STATUS,
  mentorName: "",
  country: MCountry.COUNTRY,
};

interface IState {
  requests: SessionRequest[];
  selectedRequest: SessionRequest;
  loading: boolean;
}

const initialState: IState = {
  requests: [],
  selectedRequest: {} as any,
  loading: false,
};

const initialPagination = {
  total_pages: 0,
  page_number: 0,
};

interface Orders {
  current: "REQUESTED_ON" | "MENTOR_NAME";
  requestedOn: "ASCENDING" | "DESCENDING";
  mentorName: "ASCENDING" | "DESCENDING";
}

const initialOrders: Orders = {
  requestedOn: "DESCENDING",
  mentorName: "ASCENDING",
  current: "REQUESTED_ON",
};

const MentoringSessionRequests = () => {
  const store = useFirestore();
  const appUser = useAppUser();
  const selectedCountry = useWorkshops();

  const [filters, setFilters] = useState(initialFilters);
  const [teams, setTeams] = useState<
    { name: string; id: string; memberNames: string[] }[]
  >([]);
  const [members, setMembers] = useState<{ name: string; id: string }[]>([]);

  const [requests, setRequests] = useState<SessionRequest[]>([]);
  const [state, setState] = useState(initialState);
  const [orders, setOrders] = useState(initialOrders);
  const [pagination, setPagination] = useState(initialPagination);
  const [showPreview, setShowPreview] = useState(false);

  const ITEM_COUNT_IN_PAGE: number = 8;

  useEffect(() => {
    if (state.requests.length % ITEM_COUNT_IN_PAGE === 0) {
      const totalPages = state.requests.length / ITEM_COUNT_IN_PAGE;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    } else {
      const totalPages =
        Math.floor(state.requests.length / ITEM_COUNT_IN_PAGE) + 1;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    }
  }, [state.requests]);

  useEffect(() => {
    setRequests([]);
    if (
      (appUser.userRole.isModerator && selectedCountry.country !== "DEFAULT") ||
      (!appUser.userRole.isModerator && appUser.userRole.isMember) ||
      appUser.userRole.isAdmin
    ) {
      let query:
        | firebase.firestore.Query
        | firebase.firestore.CollectionReference = store.collection(
        "sessionRequests"
      );

      if (!appUser.userRole.isAdmin)
        query = query.where(
          "country",
          "==",
          appUser.userRole.isModerator
            ? selectedCountry.country
            : appUser.userRole.isMember
            ? appUser.country
            : ""
        );

      if (!appUser.userRole.isModerator && appUser.userRole.isMember) {
        if (appUser.teamIds.length !== 0) {
          query = query.where("requestedTeamId", "in", appUser.teamIds);
        }
      }

      // query = query.where("status", "!=", RequestStatus.SCHEDULED);

      return query.onSnapshot({}, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            setRequests((ps) => {
              if (
                change.doc.get("status") !== RequestStatus.DELETED &&
                change.doc.get("status") !== RequestStatus.SCHEDULED
              ) {
                if (
                  change.doc.get("requestedTeamName") !== PublicTeam.PUBLIC_TEAM ||
                  (change.doc.get("requestedTeamName") === PublicTeam.PUBLIC_TEAM &&
                    (change.doc.get("requestedTeamMemberId") === appUser.memberId || appUser.userRole.isModerator))
                ) {
                  ps.push({
                    id: change.doc.id,
                    mentorId: change.doc.get("requestedMentorId"),
                    teamId: change.doc.get("requestedTeamId"),
                    teamName: change.doc.get("requestedTeamName"),
                    memberId: change.doc.get("requestedTeamMemberId"),
                    mentorName: change.doc.get("requestedMentorName"),
                    status: change.doc.get("status"),
                    country: change.doc.get("country"),
                    createdAt: change.doc.get("createdAt"),
                    from: "REQUEST",
                  });
                }
              }

              return Object.assign([], ps);
            });
          }
          if (
            change.type === "modified" &&
            change.doc.get("status") !== RequestStatus.DELETED &&
            change.doc.get("status") !== RequestStatus.SCHEDULED
          ) {
            setRequests((ps) => {
              const modified = ps.map((a) => {
                if (a.id === change.doc.id) {
                  return {
                    id: change.doc.id,
                    mentorId: change.doc.get("requestedMentorId"),
                    teamId: change.doc.get("requestedTeamId"),
                    teamName: change.doc.get("requestedTeamName"),
                    memberId: change.doc.get("requestedTeamMemberId"),
                    mentorName: change.doc.get("requestedMentorName"),
                    status: change.doc.get("status"),
                    country: change.doc.get("country"),
                    createdAt: change.doc.get("createdAt"),
                    from: "REQUEST",
                  };
                } else {
                  return a;
                }
              });
              return Object.assign([], modified);
            });
          }
          if (
            change.type === "removed" ||
            change.doc.get("status") === RequestStatus.DELETED ||
            change.doc.get("status") === RequestStatus.SCHEDULED
          ) {
            setRequests((ps) => {
              const removed = ps.filter((a) => a.id !== change.doc.id);
              return Object.assign([], removed);
            });
          }
        });
      });
    }
  }, [
    store,
    appUser.country,
    appUser.userRole,
    appUser.teamIds,
    appUser.memberId,
    selectedCountry.country,
  ]);

  useEffect(() => {
    setRequests([]);
    if (
      (appUser.userRole.isModerator && selectedCountry.country !== "DEFAULT") ||
      (!appUser.userRole.isModerator && appUser.userRole.isMember) ||
      appUser.userRole.isAdmin
    ) {
      let query:
        | firebase.firestore.Query
        | firebase.firestore.CollectionReference = store.collection("sessions");

      if (!appUser.userRole.isAdmin) {
        query = query.where(
          "country",
          "==",
          appUser.userRole.isModerator
            ? selectedCountry.country
            : appUser.userRole.isMember
            ? appUser.country
            : ""
        );
      }

      if (!appUser.userRole.isModerator && appUser.userRole.isMember) {
        if (appUser.teamIds.length !== 0) {
          query = query.where("teamId", "in", appUser.teamIds);
        }
      }

      // query = query.where("active", "==", true);

      return query.onSnapshot({}, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added" && change.doc.data().active === true) {
            setRequests((ps) => {
              const requestData: any = change.doc.data()
                .mentoringSessionRequest;

              if (
                requestData.requestedTeamName !== PublicTeam.PUBLIC_TEAM ||
                (requestData.requestedTeamName === PublicTeam.PUBLIC_TEAM &&
                  (requestData.requestedTeamMemberId === appUser.memberId || appUser.userRole.isModerator))
              ) {
                ps.push({
                  id: change.doc.id,
                  mentorId: requestData.requestedMentorId,
                  teamId: requestData.requestedTeamId,
                  teamName: requestData.requestedTeamName,
                  memberId: requestData.requestedTeamMemberId,
                  status:
                    (change.doc.data().endAt as firebase.firestore.Timestamp)
                      .seconds >
                    new Date().getTime() / 1000
                      ? RequestStatus.SCHEDULED
                      : RequestStatus.COMPLETED,
                  country: requestData.country,
                  mentorName: requestData.requestedMentorName,
                  endDate: change.doc.data().endDate,
                  createdAt: firebase.firestore.Timestamp.fromDate(
                    new Date(Date.parse(requestData.createdAt))
                  ),
                  from: "SESSION",
                });
              }

              return Object.assign([], ps);
            });
          }
          if (change.type === "modified" && change.doc.data().active === true) {
            setRequests((ps) => {
              const modified = ps.map((a) => {
                if (a.id === change.doc.id) {
                  const requestData: any = change.doc.data()
                    .mentoringSessionRequest;
                  return {
                    id: change.doc.id,
                    mentorId: requestData.requestedMentorId,
                    teamId: requestData.requestedTeamId,
                    teamName: requestData.requestedTeamName,
                    memberId: requestData.requestedTeamMemberId,
                    status:
                      (change.doc.data().endAt as firebase.firestore.Timestamp)
                        .seconds >
                      new Date().getTime() / 1000
                        ? RequestStatus.SCHEDULED
                        : RequestStatus.COMPLETED,
                    mentorName: requestData.requestedMentorName,
                    country: requestData.country,
                    endDate: change.doc.data().endDate,
                    createdAt: requestData.createdAt,
                    from: "SESSION",
                  };
                } else {
                  return a;
                }
              });
              return Object.assign([], modified);
            });
          }
          if (change.type === "removed" || change.doc.data().active === false) {
            setRequests((ps) => {
              const removed = ps.filter((a) => a.id !== change.doc.id);
              return Object.assign([], removed);
            });
          }
        });
      });
    }
  }, [
    store,
    appUser.country,
    appUser.userRole,
    appUser.memberId,
    appUser.teamIds,
    selectedCountry.country,
  ]);

  useEffect(() => {
    const teamIds = Array.from(new Set(requests
      // .filter((r) => r.teamName !== "PUBLIC-TEAM")
      .map((r) => r.teamId)));
    const memberIds = Array.from(new Set(requests.map((r) => r.memberId)));
    setTeams([]);
    setMembers([]);
    let unset;
    if (teamIds && teamIds.length !== 0) {
      unset = store.collection("teams").onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (teamIds.filter((tid) => tid === change.doc.id).length > 0) {
            setTeams((ps) => {
              ps.push({
                id: change.doc.id,
                name: change.doc.data().name,
                memberNames: change.doc
                  .data()
                  .members.map((m: any) => m.fullName),
              });
              return Object.assign([], ps);
            });

            setMembers((ps) => {
              const members: {
                fullName: string;
                id: string;
              }[] = change.doc.data().members;

              const d_Members = members.filter(
                (v, i, a) => a.findIndex((t) => t.id === v.id) === i
              );
              const fMembers = Array.from(
                new Set(d_Members.filter((m) => memberIds.includes(m.id)))
              );
              fMembers.forEach((fm) => {
                if (ps.filter((p) => p.id === fm.id).length === 0)
                  ps.push({ id: fm.id, name: fm.fullName });
              });
              return Object.assign([], ps);
            });
          }
        });
      });
    }

    if (unset) return unset;
  }, [store, requests]);

  useEffect(() => {
    const memberIds = requests
      .filter((r) => r.teamName === "PUBLIC-TEAM")
      .map((r) => r.memberId);
    let unset;
    if (memberIds.length > 0) {
      unset = store
        .collection("members")
        .where(firebase.firestore.FieldPath.documentId(), "in", memberIds)
        .onSnapshot((snapshot) => {
          snapshot.docChanges().forEach((change) => {
            setMembers((ps) => {
              const member = {
                name: change.doc.data().fullName,
                id: change.doc.id,
              };

              if (ps.filter((m) => m.id === member.id).length <= 0) {
                ps.push(member);
              }
              return Object.assign([], ps);
            });
          });
        });
    }

    return unset;
  }, [store, requests]);

  useEffect(() => {
    let f_request = requests;
    if (!showPreview) {
      if (orders.current === "MENTOR_NAME") {
        if (orders.mentorName === "ASCENDING") {
          f_request.sort((a: SessionRequest, b: SessionRequest) => {
            if (a.mentorName && b.mentorName) {
              return b.mentorName?.toLowerCase() > a.mentorName?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        } else {
          f_request.sort((a: SessionRequest, b: SessionRequest) => {
            if (a.mentorName && b.mentorName) {
              return b.mentorName?.toLowerCase() < a.mentorName?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        }
      } else {
        if (orders.requestedOn === "ASCENDING") {
          f_request = f_request.sort((a: any, b: any) => {
            return a.createdAt?.seconds - b.createdAt?.seconds;
          });
        } else {
          f_request = f_request.sort(
            (a: any, b: any) => b.createdAt?.seconds - a.createdAt?.seconds
          );
        }
      }

      if (filters.mentorName.trim().length > 0) {
        f_request = f_request.filter((request) => {
          if (request.mentorName) {
            return request.mentorName
              ?.toLowerCase()
              .includes(filters.mentorName.toLowerCase());
          } else {
            return true;
          }
        });
      }

      if (
        filters.status !== MRequestStatus.ALL &&
        filters.status !== MRequestStatus.STATUS
      ) {
        f_request = f_request.filter(
          (request) => request.status === (filters.status as any)
        );
      }

      if (
        filters.country !== MCountry.ALL &&
        filters.country !== MCountry.COUNTRY
      ) {
        f_request = f_request.filter(
          (request) => request.country === (filters.country as any)
        );
      }
      setState((ps) => {
        return { ...ps, requests: f_request };
      });
    }
    if (requests.length === 0)
      setState((ps) => {
        return { ...ps, requests: [] };
      });
  }, [
    filters.mentorName,
    filters.status,
    filters.country,
    requests,
    orders,
    showPreview,
  ]);

  useEffect(() => {
    setPagination((ps) => {
      return { ...ps, page_number: 0 };
    });
  }, [filters.mentorName, filters.status]);

  const paginate = (
    array: SessionRequest[],
    page_number: number,
    page_size: number
  ): SessionRequest[] =>
    array.slice(page_number * page_size, (page_number + 1) * page_size);

  return (
    <div>
      <PageHeader title="Mentoring Session Requests" />
      <Container
        fluid
        style={{
          paddingTop: "210px",
          paddingLeft: "65px",
          paddingRight: "50px",
          paddingBottom: "10px",
        }}
      >
        <Row>
          <Col
            xs={12}
            md={3}
            xl={3}
            className="d-flex align-items-center ml-md-0 my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <input
                className="app-input input-small search-img"
                style={{ paddingRight: "30px" }}
                placeholder="Mentor Name"
                value={filters.mentorName}
                onChange={(e) => {
                  setFilters({ ...filters, mentorName: e.target.value });
                }}
              />
            </div>
          </Col>
          <Col
            xs={12}
            md={3}
            xl={3}
            className="d-flex align-items-center my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <select
                className="app-input input-small filter-img"
                value={filters.status}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    status: e.target.value as any,
                  });
                }}
              >
                <option value={MRequestStatus.STATUS} disabled hidden>
                  Status
                </option>
                <option value={MRequestStatus.ALL}>All Status</option>
                <option value={MRequestStatus.PENDING}>Pending</option>
                <option value={MRequestStatus.REJECTED}>Rejected</option>
                <option value={MRequestStatus.SCHEDULED}>Scheduled</option>
                <option value={MRequestStatus.COMPLETED}>Completed</option>
              </select>
            </div>
          </Col>
          {appUser.userRole.isAdmin && (
            <Col
              xs={12}
              md={3}
              xl={3}
              className="d-flex align-items-center my-1 my-md-0"
            >
              <div style={{ flex: "1" }}>
                <select
                  className="app-input input-small filter-img"
                  value={filters.country}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      country: e.target.value as any,
                    });
                  }}
                >
                  <option value={MCountry.COUNTRY} disabled hidden>
                    Country
                  </option>
                  <option value={MCountry.ALL}>All Countries</option>
                  <option value={MCountry.BOSNIA}>Bosnia</option>
                  <option value={MCountry.INDIA}>India</option>
                  <option value={MCountry.JAMAICA}>Jamaica</option>
                  <option value={MCountry.MALAYSIA}>Malaysia</option>
                  <option value={MCountry.SRI_LANKA}>Sri Lanka</option>
                  <option value={MCountry.INDONESIA}>Indonesia</option>
                </select>
              </div>
            </Col>
          )}
          <Col
            xs={12}
            md={2}
            xl={2}
            className="d-flex align-items-center my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <button
                className="app-button"
                style={{
                  backgroundColor: "#6c757d",
                  padding: "3px 10px 3px 10px",
                }}
                onClick={() => setFilters(initialFilters)}
              >
                Clear filters
              </button>
            </div>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={12}>
            <BarLoader
              height={4}
              width={"100%"}
              color={"#006699"}
              loading={state.loading}
            />
          </Col>
        </Row>
        <Row className="mt-2 scroll" style={{ overflowX: "auto" }}>
          <Col xs={12} style={{ minHeight: "500px" }}>
            <table className="table-applications">
              <colgroup>
                <col span={1} style={{ width: "25%" }} />
                <col span={1} style={{ width: "35%" }} />
                <col span={1} style={{ width: "20%" }} />
                <col span={1} style={{ width: "20%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setOrders({
                        ...orders,
                        current: "MENTOR_NAME",
                        mentorName:
                          orders.mentorName === "ASCENDING"
                            ? "DESCENDING"
                            : "ASCENDING",
                      })
                    }
                  >
                    Mentor Name{" "}
                    <TiArrowSortedDown
                      className="order-arrow"
                      style={
                        orders.current === "MENTOR_NAME"
                          ? orders.mentorName === "ASCENDING"
                            ? { transform: "rotate(0deg)" }
                            : {
                                transform: "rotate(180deg) translateY(2px)",
                              }
                          : { color: "#aaa" }
                      }
                    />
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => {}}>
                    Team Name
                  </th>
                  {appUser.userRole.isAdmin && (
                    <th style={{ cursor: "pointer" }} onClick={() => {}}>
                      Country
                    </th>
                  )}
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setOrders({
                        ...orders,
                        current: "REQUESTED_ON",
                        requestedOn:
                          orders.requestedOn === "ASCENDING"
                            ? "DESCENDING"
                            : "ASCENDING",
                      })
                    }
                  >
                    Requested On{" "}
                    <TiArrowSortedDown
                      className="order-arrow"
                      style={
                        orders.current === "REQUESTED_ON"
                          ? orders.requestedOn === "ASCENDING"
                            ? { transform: "rotate(0deg)" }
                            : {
                                transform: "rotate(180deg) translateY(2px)",
                              }
                          : { color: "#aaa" }
                      }
                    />
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginate(
                  state.requests,
                  pagination.page_number,
                  ITEM_COUNT_IN_PAGE
                ).map((request: SessionRequest, index: number) => (
                  <tr
                    key={index}
                    className="link-label-application"
                    onClick={() => {
                      setState({ ...state, selectedRequest: request });
                      setShowPreview(true);
                    }}
                  >
                    <td>
                      <Highlighter
                        highlightClassName="highlightClass"
                        searchWords={[filters.mentorName || ""]}
                        highlightTag={({ children, highlightIndex }: any) => {
                          return (
                            <span className="highlightClass">{children}</span>
                          );
                        }}
                        autoEscape={true}
                        textToHighlight={request.mentorName || ""}
                      />
                    </td>
                    <td>
                      {request.teamName === PublicTeam.PUBLIC_TEAM
                        ? "-"
                        : request.teamName || ""}
                    </td>
                    {appUser.userRole.isAdmin && (
                      <td> {request.country.replace("_", " ") || ""} </td>
                    )}
                    <td>
                      <Moment format="MM/DD/YYYY">
                        {new Date(request.createdAt.seconds * 1000)}
                      </Moment>
                    </td>
                    <td>
                      <StatusLabelRequest status={request.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
          <Col xs={12}>
            <Pagination
              pInfo={{
                page: pagination.page_number,
                totalPages: pagination.total_pages,
              }}
              onPageChanged={(page) => {
                setPagination({ ...pagination, page_number: page });
              }}
            />
          </Col>
        </Row>
      </Container>
      {requests.length > 0 && state.selectedRequest && (
        <SessionPreview
          sessions={state.requests.map((r) => {
            const [member] = members.filter((m) => m.id === r.memberId);
            const [team] = teams.filter((m) => m.id === r.teamId);
            return {
              ...r,
              memberName: member ? member.name : "",
              mentorName: r.mentorName,
              teamName: team ? team.name : "",
              memberNames:
                team?.name !== "PUBLIC-TEAM"
                  ? team?.memberNames
                  : [member ? member.name : ""],
            };
          })}
          session={{
            ...state.selectedRequest,
            memberName: members.filter(
              (m) => m.id === state.selectedRequest.memberId
            )[0]?.name
              ? members.filter(
                  (m) => m.id === state.selectedRequest.memberId
                )[0].name
              : "",
            mentorName: state.selectedRequest.mentorName,
            teamName: teams.filter(
              (m) => m.id === state.selectedRequest.teamId
            )[0]?.name
              ? teams.filter((m) => m.id === state.selectedRequest.teamId)[0]
                  .name
              : "",
            memberNames:
              (teams.filter((m) => m.id === state.selectedRequest.teamId)[0]?.name
                ? teams.filter((m) => m.id === state.selectedRequest.teamId)[0].name
                : "") === "PUBLIC-TEAM"
                ? teams.filter((m) => m.id === state.selectedRequest.teamId)[0]?.memberNames
                  ? teams.filter((m) => m.id === state.selectedRequest.teamId)[0].memberNames
                  : []
                : [
                    members.filter((m) => m.id === state.selectedRequest.memberId)[0]?.name
                      ? members.filter((m) => m.id === state.selectedRequest.memberId)[0].name
                      : "",
                  ],
          }}
          show={showPreview}
          onHide={() => setShowPreview(false)}
          onReject={(id) => {
            setState((ps) => {
              const updated = ps.requests.map((s) => {
                if (s.id === id) {
                  return { ...s, status: RequestStatus.REJECTED };
                } else {
                  return s;
                }
              });
              return {
                ...ps,
                requests: Object.assign([], updated),
                selectedRequest: {
                  ...ps.selectedRequest,
                  status: RequestStatus.REJECTED,
                },
              };
            });
          }}
        />
      )}
    </div>
  );
};

export default MentoringSessionRequests;
